import React, { useEffect, useMemo, useState } from "react";
import {
  FaBoxOpen,
  FaCreditCard,
  FaHome,
  FaIdCard,
  FaMapMarkerAlt,
  FaReceipt,
  FaShoppingBag,
  FaSignOutAlt,
  FaTachometerAlt,
  FaUser,
} from "react-icons/fa";

import Header from "../Header";
import Footer from "../Footer";
import { useAuth } from "../../context/AuthContext";
import "./UserPanel.css";

const initialProfileForm = {
  firstName: "",
  lastName: "",
  nationalCode: "",
};

const initialAddressForm = {
  receiverName: "",
  mobile: "",
  province: "",
  city: "",
  fullAddress: "",
  plate: "",
  unit: "",
  postalCode: "",
};

const PAYMENT_INFO = {
  cardNumber: "6037-7011-1523-7676",
  cardOwner: "پریسا عبداله پور",
  receiptPhone: "09129518635",
  receiptChannels: "بله، روبیکا یا تلگرام",
};

export default function UserPanel({ navigateTo }) {
  const {
    user,
    isAuthenticated,
    authLoading,
    updateCurrentUser,
    refreshCurrentUser,
    logout,
  } = useAuth();

  const [activeTab, setActiveTab] = useState("dashboard");
  const [profileForm, setProfileForm] = useState(initialProfileForm);
  const [addresses, setAddresses] = useState([]);
  const [addressForm, setAddressForm] = useState(initialAddressForm);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [profileErrors, setProfileErrors] = useState({});
  const [addressErrors, setAddressErrors] = useState({});
  const [panelMessage, setPanelMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigateTo("login");
    }
  }, [authLoading, isAuthenticated, navigateTo]);

  useEffect(() => {
    if (!user) return;

    setProfileForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      nationalCode: user.nationalCode || "",
    });

    setAddresses(Array.isArray(user.addresses) ? user.addresses : []);
  }, [user]);

  const orders = useMemo(() => {
    const safeOrders = Array.isArray(user?.orders) ? user.orders : [];

    return [...safeOrders].sort((a, b) => {
      const dateA = new Date(a.createdAt || 0).getTime();
      const dateB = new Date(b.createdAt || 0).getTime();

      return dateB - dateA;
    });
  }, [user]);

  const payments = useMemo(() => {
    return orders.map((order) => ({
      id: `payment-${order.id}`,
      orderId: order.id,
      amount: order.finalTotal || order.total || 0,
      paymentStatus: order.paymentStatus || "pending",
      paymentMethod: order.paymentMethod || "card_to_card",
      createdAt: order.paidAt || order.createdAt,
    }));
  }, [orders]);

  const stats = useMemo(() => {
    const paidOrders = orders.filter((order) => order.paymentStatus === "paid");
    const pendingOrders = orders.filter(
      (order) => order.paymentStatus !== "paid"
    );

    const totalPaid = paidOrders.reduce((total, order) => {
      return total + Number(order.finalTotal || order.total || 0);
    }, 0);

    return {
      ordersCount: orders.length,
      paidOrdersCount: paidOrders.length,
      pendingOrdersCount: pendingOrders.length,
      addressesCount: addresses.length,
      totalPaid,
    };
  }, [orders, addresses]);

  const normalizeDigits = (value) => {
    return String(value || "").replace(/[^\d]/g, "");
  };

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString("fa-IR");
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return "-";

    try {
      return new Intl.DateTimeFormat("fa-IR-u-ca-persian", {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(dateValue));
    } catch {
      return "-";
    }
  };

  const getDisplayName = () => {
    const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

    return fullName || user?.username || "کاربر نقش خیال";
  };

  const getOrderStatusLabel = (status) => {
    switch (status) {
      case "waiting_for_payment":
        return "در انتظار پرداخت";
      case "paid":
        return "پرداخت‌شده";
      case "processing":
        return "در حال آماده‌سازی";
      case "ready_to_ship":
        return "آماده ارسال";
      case "shipped":
        return "ارسال‌شده";
      case "delivered":
        return "تحویل‌شده";
      case "cancelled":
        return "لغوشده";
      default:
        return "ثبت‌شده";
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case "paid":
        return "پرداخت موفق";
      case "failed":
        return "پرداخت ناموفق";
      case "refunded":
        return "بازگشت وجه";
      case "pending":
      default:
        return "در انتظار پرداخت";
    }
  };

  const getPaymentMethodLabel = (method) => {
    switch (method) {
      case "online_gateway":
        return "درگاه آنلاین";
      case "card_to_card":
        return "کارت به کارت";
      case "cash_on_delivery":
        return "پرداخت هنگام تحویل";
      case "online_gateway_pending":
        return "درگاه آنلاین / غیرفعال";
      default:
        return "کارت به کارت / بررسی دستی";
    }
  };

  const showMessage = (type, text) => {
    setPanelMessage({ type, text });

    setTimeout(() => {
      setPanelMessage(null);
    }, 3500);
  };

  const handleLogout = () => {
    logout();
    navigateTo("shop");
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;

    if (name === "nationalCode") {
      setProfileForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 10),
      }));
    } else {
      setProfileForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (profileErrors[name]) {
      setProfileErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateProfile = () => {
    const errors = {};

    if (!profileForm.firstName.trim()) {
      errors.firstName = "نام الزامی است";
    }

    if (!profileForm.lastName.trim()) {
      errors.lastName = "نام خانوادگی الزامی است";
    }

    if (
      profileForm.nationalCode &&
      !/^[0-9]{10}$/.test(profileForm.nationalCode)
    ) {
      errors.nationalCode = "کد ملی باید ۱۰ رقم باشد";
    }

    setProfileErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const saveProfile = async () => {
    if (!validateProfile()) return;

    setSaving(true);

    try {
      const result = await updateCurrentUser({
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
        nationalCode: profileForm.nationalCode.trim(),
        updatedAt: new Date().toISOString(),
      });

      if (result.success) {
        if (refreshCurrentUser) {
          await refreshCurrentUser();
        }

        showMessage("success", "اطلاعات حساب با موفقیت ذخیره شد.");
      } else {
        showMessage("error", result.message || "خطا در ذخیره اطلاعات حساب.");
      }
    } catch (error) {
      console.error("Save profile error:", error);
      showMessage("error", "خطا در ذخیره اطلاعات حساب.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      setAddressForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 11),
      }));
    } else if (name === "postalCode") {
      setAddressForm((prev) => ({
        ...prev,
        [name]: normalizeDigits(value).slice(0, 10),
      }));
    } else {
      setAddressForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    if (addressErrors[name]) {
      setAddressErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateAddress = () => {
    const errors = {};

    if (!addressForm.receiverName.trim()) {
      errors.receiverName = "نام گیرنده الزامی است";
    }

    if (!addressForm.mobile.trim()) {
      errors.mobile = "شماره موبایل الزامی است";
    } else if (!/^[0-9]{11}$/.test(addressForm.mobile)) {
      errors.mobile = "شماره موبایل باید ۱۱ رقم باشد";
    }

    if (!addressForm.province.trim()) {
      errors.province = "استان الزامی است";
    }

    if (!addressForm.city.trim()) {
      errors.city = "شهر الزامی است";
    }

    if (!addressForm.fullAddress.trim()) {
      errors.fullAddress = "آدرس کامل الزامی است";
    }

    if (!addressForm.plate.trim()) {
      errors.plate = "پلاک الزامی است";
    }

    if (!addressForm.postalCode.trim()) {
      errors.postalCode = "کد پستی الزامی است";
    } else if (!/^[0-9]{10}$/.test(addressForm.postalCode)) {
      errors.postalCode = "کد پستی باید ۱۰ رقم باشد";
    }

    setAddressErrors(errors);

    return Object.keys(errors).length === 0;
  };

  const resetAddressForm = () => {
    setAddressForm(initialAddressForm);
    setEditingAddressId(null);
    setAddressErrors({});
    setShowAddressForm(false);
  };

  const openNewAddressForm = () => {
    setAddressForm({
      ...initialAddressForm,
      receiverName: getDisplayName(),
      mobile: user?.phone || "",
    });
    setEditingAddressId(null);
    setAddressErrors({});
    setShowAddressForm(true);
  };

  const openEditAddressForm = (address) => {
    setAddressForm({
      receiverName: address.receiverName || "",
      mobile: address.mobile || "",
      province: address.province || "",
      city: address.city || "",
      fullAddress: address.fullAddress || "",
      plate: address.plate || "",
      unit: address.unit || "",
      postalCode: address.postalCode || "",
    });

    setEditingAddressId(address.id);
    setAddressErrors({});
    setShowAddressForm(true);
  };

  const saveAddress = async () => {
    if (!validateAddress()) return;

    setSaving(true);

    const now = new Date().toISOString();

    const cleanedAddress = {
      id: editingAddressId || `addr-${Date.now()}`,
      receiverName: addressForm.receiverName.trim(),
      mobile: addressForm.mobile.trim(),
      province: addressForm.province.trim(),
      city: addressForm.city.trim(),
      fullAddress: addressForm.fullAddress.trim(),
      plate: addressForm.plate.trim(),
      unit: addressForm.unit.trim(),
      postalCode: addressForm.postalCode.trim(),
      isDefault:
        addresses.length === 0 ||
        addresses.find((item) => item.id === editingAddressId)?.isDefault ||
        false,
      createdAt:
        addresses.find((item) => item.id === editingAddressId)?.createdAt ||
        now,
      updatedAt: now,
    };

    const nextAddresses = editingAddressId
      ? addresses.map((address) =>
          address.id === editingAddressId ? cleanedAddress : address
        )
      : [...addresses, cleanedAddress];

    try {
      const result = await updateCurrentUser({
        addresses: nextAddresses,
        updatedAt: now,
      });

      if (result.success) {
        setAddresses(nextAddresses);

        if (refreshCurrentUser) {
          await refreshCurrentUser();
        }

        resetAddressForm();
        showMessage("success", "آدرس با موفقیت ذخیره شد.");
      } else {
        showMessage("error", result.message || "خطا در ذخیره آدرس.");
      }
    } catch (error) {
      console.error("Save address error:", error);
      showMessage("error", "خطا در ذخیره آدرس.");
    } finally {
      setSaving(false);
    }
  };

  const setDefaultAddress = async (addressId) => {
    const nextAddresses = addresses.map((address) => ({
      ...address,
      isDefault: address.id === addressId,
    }));

    setSaving(true);

    try {
      const result = await updateCurrentUser({
        addresses: nextAddresses,
        updatedAt: new Date().toISOString(),
      });

      if (result.success) {
        setAddresses(nextAddresses);

        if (refreshCurrentUser) {
          await refreshCurrentUser();
        }

        showMessage("success", "آدرس پیش‌فرض تغییر کرد.");
      } else {
        showMessage("error", result.message || "خطا در تغییر آدرس پیش‌فرض.");
      }
    } catch (error) {
      console.error("Set default address error:", error);
      showMessage("error", "خطا در تغییر آدرس پیش‌فرض.");
    } finally {
      setSaving(false);
    }
  };

  const deleteAddress = async (addressId) => {
    const targetAddress = addresses.find((address) => address.id === addressId);

    if (!targetAddress) return;

    if (!window.confirm("آیا از حذف این آدرس مطمئن هستید؟")) {
      return;
    }

    let nextAddresses = addresses.filter((address) => address.id !== addressId);

    if (targetAddress.isDefault && nextAddresses.length > 0) {
      nextAddresses = nextAddresses.map((address, index) => ({
        ...address,
        isDefault: index === 0,
      }));
    }

    setSaving(true);

    try {
      const result = await updateCurrentUser({
        addresses: nextAddresses,
        updatedAt: new Date().toISOString(),
      });

      if (result.success) {
        setAddresses(nextAddresses);

        if (refreshCurrentUser) {
          await refreshCurrentUser();
        }

        showMessage("success", "آدرس حذف شد.");
      } else {
        showMessage("error", result.message || "خطا در حذف آدرس.");
      }
    } catch (error) {
      console.error("Delete address error:", error);
      showMessage("error", "خطا در حذف آدرس.");
    } finally {
      setSaving(false);
    }
  };

  if (authLoading) {
    return (
      <>
        <Header navigateTo={navigateTo} />
        <main className="user-panel-page">
          <div className="user-panel-loading">در حال بارگذاری پنل کاربری...</div>
        </main>
        <Footer navigateTo={navigateTo} />
      </>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <>
      <Header navigateTo={navigateTo} />

      <main className="user-panel-page">
        <section className="user-panel-hero">
          <div>
            <span>پنل کاربری نقش خیال</span>
            <h1>سلام {getDisplayName()} عزیز</h1>
            <p>
              در این بخش می‌توانید سفارش‌ها، وضعیت پرداخت‌ها و اطلاعات حساب
              کاربری خود را مشاهده و مدیریت کنید.
            </p>
          </div>

          <button type="button" className="user-panel-logout-btn" onClick={handleLogout}>
            <FaSignOutAlt />
            خروج از حساب
          </button>
        </section>

        {panelMessage && (
          <div className={`user-panel-message ${panelMessage.type}`}>
            {panelMessage.text}
          </div>
        )}

        <section className="user-panel-layout">
          <aside className="user-panel-sidebar">
            <div className="user-panel-profile-card">
              <div className="user-panel-avatar">
                <FaUser />
              </div>

              <h2>{getDisplayName()}</h2>
              <p dir="ltr">{user.phone || user.email || "-"}</p>
            </div>

            <nav className="user-panel-menu">
              <button
                type="button"
                className={activeTab === "dashboard" ? "active" : ""}
                onClick={() => setActiveTab("dashboard")}
              >
                <FaTachometerAlt />
                داشبورد
              </button>

              <button
                type="button"
                className={activeTab === "orders" ? "active" : ""}
                onClick={() => setActiveTab("orders")}
              >
                <FaBoxOpen />
                سفارش‌های من
              </button>

              <button
                type="button"
                className={activeTab === "payments" ? "active" : ""}
                onClick={() => setActiveTab("payments")}
              >
                <FaCreditCard />
                پرداخت‌های من
              </button>

              <button
                type="button"
                className={activeTab === "account" ? "active" : ""}
                onClick={() => setActiveTab("account")}
              >
                <FaIdCard />
                اطلاعات حساب
              </button>
            </nav>
          </aside>

          <section className="user-panel-content">
            {activeTab === "dashboard" && (
              <>
                <section className="user-panel-stats">
                  <div className="user-stat-card">
                    <FaShoppingBag />
                    <div>
                      <strong>{stats.ordersCount}</strong>
                      <span>کل سفارش‌ها</span>
                    </div>
                  </div>

                  <div className="user-stat-card">
                    <FaCreditCard />
                    <div>
                      <strong>{stats.paidOrdersCount}</strong>
                      <span>پرداخت موفق</span>
                    </div>
                  </div>

                  <div className="user-stat-card">
                    <FaReceipt />
                    <div>
                      <strong>{stats.pendingOrdersCount}</strong>
                      <span>در انتظار پرداخت</span>
                    </div>
                  </div>

                  <div className="user-stat-card">
                    <FaMapMarkerAlt />
                    <div>
                      <strong>{stats.addressesCount}</strong>
                      <span>آدرس ثبت‌شده</span>
                    </div>
                  </div>
                </section>

                <section className="user-panel-card">
                  <div className="user-section-header">
                    <div>
                      <h2>آخرین سفارش‌ها</h2>
                      <p>مروری سریع بر آخرین سفارش‌های ثبت‌شده شما</p>
                    </div>

                    <button
                      type="button"
                      className="user-panel-small-btn"
                      onClick={() => setActiveTab("orders")}
                    >
                      مشاهده همه
                    </button>
                  </div>

                  {orders.length === 0 ? (
                    <div className="user-empty-state">
                      هنوز سفارشی ثبت نکرده‌اید.
                    </div>
                  ) : (
                    <div className="user-orders-list compact">
                      {orders.slice(0, 3).map((order) => (
                        <div className="user-order-card" key={order.id}>
                          <div>
                            <h3>{order.id}</h3>
                            <p>{formatDate(order.createdAt)}</p>
                          </div>

                          <div>
                            <span className={`status-badge ${order.orderStatus || "default"}`}>
                              {getOrderStatusLabel(order.orderStatus)}
                            </span>
                            <strong>
                              {formatPrice(order.finalTotal || order.total)} تومان
                            </strong>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}

            {activeTab === "orders" && (
              <section className="user-panel-card">
                <div className="user-section-header">
                  <div>
                    <h2>سفارش‌های من</h2>
                    <p>همه سفارش‌هایی که در فروشگاه نقش خیال ثبت کرده‌اید</p>
                  </div>
                </div>

                {orders.length === 0 ? (
                  <div className="user-empty-state">
                    هنوز سفارشی ثبت نکرده‌اید.
                    <button type="button" onClick={() => navigateTo("shop")}>
                      رفتن به فروشگاه
                    </button>
                  </div>
                ) : (
                  <div className="user-orders-list">
                    {orders.map((order) => (
                      <article className="user-order-detail-card" key={order.id}>
                        <div className="user-order-top">
                          <div>
                            <span>شماره سفارش</span>
                            <h3 dir="ltr">{order.id}</h3>
                          </div>

                          <div className="user-order-badges">
                            <span className={`status-badge ${order.orderStatus || "default"}`}>
                              {getOrderStatusLabel(order.orderStatus)}
                            </span>

                            <span className={`payment-badge ${order.paymentStatus || "pending"}`}>
                              {getPaymentStatusLabel(order.paymentStatus)}
                            </span>
                          </div>
                        </div>

                        <div className="user-order-products">
                          {Array.isArray(order.items) &&
                            order.items.map((item) => (
                              <div className="user-order-product" key={`${order.id}-${item.id}`}>
                                <img src={item.img} alt={item.title} />
                                <div>
                                  <strong>{item.title}</strong>
                                  <span>
                                    تعداد: {item.quantity} / قیمت:{" "}
                                    {formatPrice(item.price)} تومان
                                  </span>
                                </div>
                              </div>
                            ))}
                        </div>

                        <div className="user-order-info-grid">
                          <div>
                            <span>تاریخ ثبت</span>
                            <strong>{formatDate(order.createdAt)}</strong>
                          </div>

                          <div>
                            <span>روش ارسال</span>
                            <strong>{order.shippingTitle || "-"}</strong>
                          </div>

                          <div>
                            <span>زمان تحویل</span>
                            <strong>{order.deliveryTime || "-"}</strong>
                          </div>

                          <div>
                            <span>مبلغ نهایی</span>
                            <strong>
                              {formatPrice(order.finalTotal || order.total)} تومان
                            </strong>
                          </div>
                        </div>

                        {order.address && (
                          <div className="user-order-address">
                            <FaHome />
                            <p>
                              {order.address.province}، {order.address.city}،{" "}
                              {order.address.fullAddress}، پلاک{" "}
                              {order.address.plate}
                              {order.address.unit
                                ? `، واحد ${order.address.unit}`
                                : ""}
                              {" — "}
                              کد پستی:{" "}
                              <span dir="ltr">{order.address.postalCode}</span>
                            </p>
                          </div>
                        )}

                        {order.paymentStatus !== "paid" && (
                          <div className="user-order-address">
                            <FaCreditCard />
                            <div style={{ lineHeight: "2.1" }}>
                              <p>
                                <strong>راهنمای پرداخت:</strong> برای نهایی شدن
                                سفارش، مبلغ{" "}
                                <strong>
                                  {formatPrice(order.finalTotal || order.total)} تومان
                                </strong>{" "}
                                را به کارت زیر واریز کنید.
                              </p>

                              <p>
                                شماره کارت:{" "}
                                <strong dir="ltr">
                                  {order.paymentInfo?.cardNumber ||
                                    PAYMENT_INFO.cardNumber}
                                </strong>
                              </p>

                              <p>
                                به نام:{" "}
                                <strong>
                                  {order.paymentInfo?.cardOwner ||
                                    PAYMENT_INFO.cardOwner}
                                </strong>
                              </p>

                              <p>
                                تصویر رسید پرداخت را در پیام‌رسان‌های{" "}
                                <strong>
                                  {PAYMENT_INFO.receiptChannels}
                                </strong>{" "}
                                به شماره{" "}
                                <strong dir="ltr">
                                  {order.paymentInfo?.receiptPhone ||
                                    PAYMENT_INFO.receiptPhone}
                                </strong>{" "}
                                ارسال کنید.
                              </p>

                              <p>
                                پس از بررسی رسید، وضعیت سفارش شما به{" "}
                                <strong>پرداخت شد</strong> تغییر می‌کند.
                              </p>
                            </div>
                          </div>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )}

            {activeTab === "payments" && (
              <section className="user-panel-card">
                <div className="user-section-header">
                  <div>
                    <h2>پرداخت‌های من</h2>
                    <p>وضعیت پرداخت سفارش‌های شما</p>
                  </div>
                </div>

                {payments.length === 0 ? (
                  <div className="user-empty-state">
                    هنوز پرداختی برای شما ثبت نشده است.
                  </div>
                ) : (
                  <div className="user-table-wrap">
                    <table className="user-panel-table">
                      <thead>
                        <tr>
                          <th>شماره سفارش</th>
                          <th>تاریخ</th>
                          <th>روش پرداخت</th>
                          <th>مبلغ</th>
                          <th>وضعیت</th>
                        </tr>
                      </thead>

                      <tbody>
                        {payments.map((payment) => (
                          <tr key={payment.id}>
                            <td dir="ltr">{payment.orderId}</td>
                            <td>{formatDate(payment.createdAt)}</td>
                            <td>{getPaymentMethodLabel(payment.paymentMethod)}</td>
                            <td>{formatPrice(payment.amount)} تومان</td>
                            <td>
                              <span className={`payment-badge ${payment.paymentStatus}`}>
                                {getPaymentStatusLabel(payment.paymentStatus)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {activeTab === "account" && (
              <section className="user-panel-card">
                <div className="user-section-header">
                  <div>
                    <h2>اطلاعات حساب</h2>
                    <p>مشخصات فردی و آدرس‌های تحویل سفارش شما</p>
                  </div>
                </div>

                <div className="user-account-grid">
                  <label className="user-field">
                    <span>نام</span>
                    <input
                      type="text"
                      name="firstName"
                      value={profileForm.firstName}
                      onChange={handleProfileChange}
                    />
                    {profileErrors.firstName && (
                      <small>{profileErrors.firstName}</small>
                    )}
                  </label>

                  <label className="user-field">
                    <span>نام خانوادگی</span>
                    <input
                      type="text"
                      name="lastName"
                      value={profileForm.lastName}
                      onChange={handleProfileChange}
                    />
                    {profileErrors.lastName && (
                      <small>{profileErrors.lastName}</small>
                    )}
                  </label>

                  <label className="user-field">
                    <span>شماره موبایل</span>
                    <input type="text" value={user.phone || ""} disabled dir="ltr" />
                  </label>

                  <label className="user-field">
                    <span>ایمیل</span>
                    <input type="email" value={user.email || ""} disabled dir="ltr" />
                  </label>

                  <label className="user-field">
                    <span>کد ملی</span>
                    <input
                      type="text"
                      name="nationalCode"
                      value={profileForm.nationalCode}
                      onChange={handleProfileChange}
                      placeholder="۱۰ رقم"
                      maxLength="10"
                      dir="ltr"
                    />
                    {profileErrors.nationalCode && (
                      <small>{profileErrors.nationalCode}</small>
                    )}
                  </label>
                </div>

                <div className="user-account-actions">
                  <button
                    type="button"
                    className="user-panel-primary-btn"
                    onClick={saveProfile}
                    disabled={saving}
                  >
                    {saving ? "در حال ذخیره..." : "ذخیره اطلاعات حساب"}
                  </button>
                </div>

                <div className="user-address-section">
                  <div className="user-section-header nested">
                    <div>
                      <h2>آدرس‌های تحویل سفارش</h2>
                      <p>آدرس‌ها داخل اطلاعات حساب کاربری مدیریت می‌شوند.</p>
                    </div>

                    <button
                      type="button"
                      className="user-panel-small-btn"
                      onClick={openNewAddressForm}
                    >
                      + افزودن آدرس
                    </button>
                  </div>

                  {addresses.length === 0 ? (
                    <div className="user-empty-state">
                      هنوز آدرسی ثبت نکرده‌اید.
                    </div>
                  ) : (
                    <div className="user-address-list">
                      {addresses.map((address) => (
                        <div className="user-address-card" key={address.id}>
                          <div>
                            <div className="user-address-title-row">
                              <strong>{address.receiverName}</strong>
                              {address.isDefault && (
                                <span>آدرس پیش‌فرض</span>
                              )}
                            </div>

                            <p dir="ltr">{address.mobile}</p>

                            <p>
                              {address.province}، {address.city}،{" "}
                              {address.fullAddress}، پلاک {address.plate}
                              {address.unit ? `، واحد ${address.unit}` : ""}
                            </p>

                            <p>
                              کد پستی:{" "}
                              <span dir="ltr">{address.postalCode}</span>
                            </p>
                          </div>

                          <div className="user-address-actions">
                            {!address.isDefault && (
                              <button
                                type="button"
                                onClick={() => setDefaultAddress(address.id)}
                                disabled={saving}
                              >
                                پیش‌فرض
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={() => openEditAddressForm(address)}
                              disabled={saving}
                            >
                              ویرایش
                            </button>

                            <button
                              type="button"
                              className="danger"
                              onClick={() => deleteAddress(address.id)}
                              disabled={saving}
                            >
                              حذف
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {showAddressForm && (
                    <div className="user-address-form-card">
                      <h3>{editingAddressId ? "ویرایش آدرس" : "افزودن آدرس جدید"}</h3>

                      <div className="user-account-grid">
                        <label className="user-field">
                          <span>نام گیرنده</span>
                          <input
                            type="text"
                            name="receiverName"
                            value={addressForm.receiverName}
                            onChange={handleAddressChange}
                          />
                          {addressErrors.receiverName && (
                            <small>{addressErrors.receiverName}</small>
                          )}
                        </label>

                        <label className="user-field">
                          <span>شماره موبایل گیرنده</span>
                          <input
                            type="tel"
                            name="mobile"
                            value={addressForm.mobile}
                            onChange={handleAddressChange}
                            dir="ltr"
                          />
                          {addressErrors.mobile && (
                            <small>{addressErrors.mobile}</small>
                          )}
                        </label>

                        <label className="user-field">
                          <span>استان</span>
                          <input
                            type="text"
                            name="province"
                            value={addressForm.province}
                            onChange={handleAddressChange}
                          />
                          {addressErrors.province && (
                            <small>{addressErrors.province}</small>
                          )}
                        </label>

                        <label className="user-field">
                          <span>شهر</span>
                          <input
                            type="text"
                            name="city"
                            value={addressForm.city}
                            onChange={handleAddressChange}
                          />
                          {addressErrors.city && (
                            <small>{addressErrors.city}</small>
                          )}
                        </label>

                        <label className="user-field full">
                          <span>آدرس کامل</span>
                          <textarea
                            name="fullAddress"
                            value={addressForm.fullAddress}
                            onChange={handleAddressChange}
                            rows="3"
                          />
                          {addressErrors.fullAddress && (
                            <small>{addressErrors.fullAddress}</small>
                          )}
                        </label>

                        <label className="user-field">
                          <span>پلاک</span>
                          <input
                            type="text"
                            name="plate"
                            value={addressForm.plate}
                            onChange={handleAddressChange}
                          />
                          {addressErrors.plate && (
                            <small>{addressErrors.plate}</small>
                          )}
                        </label>

                        <label className="user-field">
                          <span>واحد</span>
                          <input
                            type="text"
                            name="unit"
                            value={addressForm.unit}
                            onChange={handleAddressChange}
                          />
                        </label>

                        <label className="user-field">
                          <span>کد پستی</span>
                          <input
                            type="text"
                            name="postalCode"
                            value={addressForm.postalCode}
                            onChange={handleAddressChange}
                            maxLength="10"
                            dir="ltr"
                          />
                          {addressErrors.postalCode && (
                            <small>{addressErrors.postalCode}</small>
                          )}
                        </label>
                      </div>

                      <div className="user-account-actions">
                        <button
                          type="button"
                          className="user-panel-primary-btn"
                          onClick={saveAddress}
                          disabled={saving}
                        >
                          {saving ? "در حال ذخیره..." : "ذخیره آدرس"}
                        </button>

                        <button
                          type="button"
                          className="user-panel-secondary-btn"
                          onClick={resetAddressForm}
                          disabled={saving}
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            )}
          </section>
        </section>
      </main>

      <Footer navigateTo={navigateTo} />
    </>
  );
}